(() => {
  const STORAGE_KEY = 'salee:wishlist';
  const ROOT_SELECTOR = '[data-salee-wishlist]';

  function readWishlist() {
    try {
      const items = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(items) ? items.filter((item) => item && typeof item === 'object') : [];
    } catch {
      return [];
    }
  }

  function writeWishlist(items) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
      document.dispatchEvent(new CustomEvent('salee:wishlist:updated', { detail: { itemCount: items.length } }));
      return true;
    } catch {
      return false;
    }
  }

  function productEndpoint(handle, root) {
    const rootPath = root.dataset.routesRoot || '/';
    const basePath = rootPath.endsWith('/') ? rootPath : `${rootPath}/`;
    return new URL(`${basePath}products/${encodeURIComponent(handle)}.js`, window.location.origin).href;
  }

  function productLink(item, product, root) {
    const candidate = product?.url || item.url;

    if (candidate) {
      try {
        const url = new URL(candidate, window.location.origin);
        if (url.origin === window.location.origin) return url.href;
      } catch {
        // Fall through to a same-store product URL.
      }
    }

    if (!item.handle) return '';

    const rootPath = root.dataset.routesRoot || '/';
    const basePath = rootPath.endsWith('/') ? rootPath : `${rootPath}/`;
    return new URL(`${basePath}products/${encodeURIComponent(item.handle)}`, window.location.origin).href;
  }

  async function fetchProduct(item, root) {
    if (!item.handle) return { item, product: null };

    try {
      const response = await fetch(productEndpoint(item.handle, root), {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });

      if (!response.ok) throw new Error(`Product request failed: ${response.status}`);
      return { item, product: await response.json() };
    } catch {
      return { item, product: null };
    }
  }

  function formatPrice(cents, root) {
    const amount = Number(cents);
    if (!Number.isFinite(amount)) return '';

    try {
      return new Intl.NumberFormat(root.dataset.locale || document.documentElement.lang || 'en', {
        style: 'currency',
        currency: root.dataset.currencyCode || 'PKR',
        maximumFractionDigits: 0,
      }).format(amount / 100);
    } catch {
      return `Rs. ${(amount / 100).toLocaleString()}`;
    }
  }

  function element(tagName, className, text) {
    const node = document.createElement(tagName);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  }

  function createCard(result, root) {
    const { item, product } = result;
    const title = product?.title || item.title || 'Saved product';
    const image = product?.featured_image || product?.images?.[0] || item.image;
    const price = product?.price ?? item.price;
    const available = product?.available === true;
    const destination = productLink(item, product, root);
    const hasProductLink = Boolean(destination);
    const card = element('article', `salee-wishlist-card${product ? '' : ' salee-wishlist-card--unavailable'}`);
    const media = hasProductLink ? document.createElement('a') : element('div');

    media.className = 'salee-wishlist-card__media';
    if (hasProductLink) {
      media.href = destination;
      media.setAttribute('aria-label', `View ${title}`);
    }

    if (image) {
      const imageElement = document.createElement('img');
      imageElement.src = image;
      imageElement.alt = title;
      imageElement.loading = 'lazy';
      imageElement.width = 720;
      imageElement.height = 900;
      media.append(imageElement);
    } else {
      media.append(element('span', 'salee-wishlist-card__placeholder', 'Image unavailable'));
    }

    const content = element('div', 'salee-wishlist-card__content');
    const titleElement = hasProductLink ? document.createElement('a') : element('h2');
    titleElement.className = 'salee-wishlist-card__title';
    titleElement.textContent = title;
    if (hasProductLink) titleElement.href = destination;
    content.append(titleElement);

    const priceElement = element('p', 'salee-wishlist-card__price', formatPrice(price, root));
    if (priceElement.textContent) content.append(priceElement);

    const availability = element(
      'p',
      `salee-wishlist-card__availability${available ? '' : ' salee-wishlist-card__availability--unavailable'}`,
      product
        ? (available ? 'In stock' : 'Currently sold out')
        : (hasProductLink ? 'Product details could not be refreshed' : 'Product unavailable')
    );
    content.append(availability);

    const actions = element('div', 'salee-wishlist-card__actions');
    if (hasProductLink) {
      const viewProduct = element('a', 'salee-wishlist-card__view', 'View product');
      viewProduct.href = destination;
      actions.append(viewProduct);
    }

    const remove = element('button', 'salee-wishlist-card__remove', 'Remove');
    remove.type = 'button';
    remove.dataset.wishlistRemove = String(item.id || item.handle || title);
    remove.setAttribute('aria-label', `Remove ${title} from wishlist`);
    actions.append(remove);

    content.append(actions);
    card.append(media, content);
    return card;
  }

  function updateCount(root, count) {
    const countElement = root.querySelector('[data-wishlist-count]');
    if (!countElement) return;

    countElement.textContent = `${count} saved ${count === 1 ? 'item' : 'items'}`;
  }

  async function render(root) {
    const grid = root.querySelector('[data-wishlist-grid]');
    const emptyState = root.querySelector('[data-wishlist-empty]');
    const loadingState = root.querySelector('[data-wishlist-loading]');
    if (!grid || !emptyState || !loadingState) return;

    const items = readWishlist();
    const renderId = Number(root.dataset.renderId || '0') + 1;
    root.dataset.renderId = String(renderId);
    updateCount(root, items.length);

    if (items.length === 0) {
      grid.replaceChildren();
      grid.hidden = true;
      loadingState.hidden = true;
      emptyState.hidden = false;
      return;
    }

    emptyState.hidden = true;
    grid.hidden = false;
    loadingState.hidden = false;
    loadingState.textContent = 'Loading your saved products...';
    grid.setAttribute('aria-busy', 'true');

    const products = await Promise.all(items.map((item) => fetchProduct(item, root)));
    if (!root.isConnected || root.dataset.renderId !== String(renderId)) return;

    grid.replaceChildren(...products.map((result) => createCard(result, root)));
    grid.removeAttribute('aria-busy');
    loadingState.hidden = true;
  }

  function removeItem(root, identifier) {
    const items = readWishlist();
    const remaining = items.filter((item) => String(item.id || item.handle || item.title) !== identifier);
    if (remaining.length === items.length || !writeWishlist(remaining)) return;
  }

  function setup(root) {
    if (root.dataset.ready === 'true') return;
    root.dataset.ready = 'true';

    root.addEventListener('click', (event) => {
      const removeButton = event.target.closest('[data-wishlist-remove]');
      if (!removeButton || !root.contains(removeButton)) return;
      removeItem(root, removeButton.dataset.wishlistRemove || '');
    });

    render(root);
  }

  function setupAll(scope = document) {
    scope.querySelectorAll(ROOT_SELECTOR).forEach(setup);
  }

  setupAll();
  document.addEventListener('salee:wishlist:updated', () => {
    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      setup(root);
      render(root);
    });
  });
  window.addEventListener('storage', (event) => {
    if (event.key === STORAGE_KEY) document.querySelectorAll(ROOT_SELECTOR).forEach(render);
  });
  document.addEventListener('shopify:section:load', (event) => setupAll(event.target));
})();
